import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { linksFromParam } from '../publications/lib/journal';
import {
  commentModerationApi,
  type ClassifyResult,
  type DeletionRecord,
  type ModerationComment,
} from '../../shared/api/comment-moderation.api';
import { nextBatch, withoutGone } from './lib/delete-queue';
import { failedPost, mergeClassify } from './lib/merge-classify';
import { humanReason, type ReasonTone } from './lib/verdict-text';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';

/**
 * Разбор комментариев под нашими публикациями.
 *
 * Вердикт ставит модель, но удаление всегда подтверждает человек: галочки
 * проставлены заранее по её решению, снять их можно до нажатия кнопки.
 * Необратимую операцию не запускаем по одному клику из списка.
 *
 * Удаляем пачками: столько же комментариев подряд удаляет CLI между
 * перерывами, дальше Instagram включает антиспам. Паузу между пачками держит
 * человек, которому нужно нажать кнопку снова, — поэтому пачки не склеиваются
 * автоматически.
 *
 * Разбираем по одной ссылке за раз. Пять ссылок одним запросом — это две
 * минуты серого прямоугольника, за которым не видно, идёт работа или всё
 * встало; и упавшая пятая ссылка уносила с собой разобранные четыре.
 *
 * «Оставить» — такой же раскрываемый список, как остальные, а не фраза
 * «нарушений не найдено». По замерам стенда модель ловит около сорока
 * процентов критики власти: в этой корзине лежит примерно половина того, что
 * человек как раз и ищет, и не дать её открыть — значит соврать про разбор.
 */

/** Корзины разбора. «Промолчала» — не вердикт сервера, а наш разбор причины. */
const BUCKETS = [
  { key: 'delete', title: 'Под удаление', note: 'Модель уверена, галочки проставлены заранее.' },
  { key: 'review', title: 'На проверку', note: 'Модель колеблется — решает человек.' },
  {
    key: 'silent',
    title: 'Модель промолчала',
    note: 'Ответа по этим комментариям нет вовсе. Их никто не смотрел.',
  },
  {
    key: 'keep',
    title: 'Оставить',
    note:
      'Нарушений модель не нашла. Она ловит примерно сорок процентов критики власти и совсем не ' +
      'понимает казахский латиницей («jaqsy», «qalai») — если ищете именно это, смотрите список.',
  },
] as const;

type Bucket = (typeof BUCKETS)[number]['key'];

/** Цвет подписи причины: уверенность модели видно раньше, чем прочитан текст. */
const TONE_CLASS: Record<ReasonTone, string> = {
  sure: 'text-destructive',
  likely: 'text-amber-600 dark:text-amber-500',
  edge: 'text-muted-foreground',
  silent: 'text-amber-600 dark:text-amber-500',
  none: 'text-muted-foreground',
};

/** Пока не пришёл policy — столько же, сколько принимает сервер. */
const DEFAULT_BATCH = 50;

/** Разбор стоит денег и двух минут, поэтому переживает перезагрузку страницы. */
const SAVED_KEY = 'instafarm:comment-moderation';

interface SavedRun {
  at: string;
  links: string;
  result: ClassifyResult;
  selected: string[];
}

function readSaved(): SavedRun | null {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    return raw ? (JSON.parse(raw) as SavedRun) : null;
  } catch {
    return null;
  }
}

function saveRun(run: SavedRun | null): void {
  try {
    if (run) localStorage.setItem(SAVED_KEY, JSON.stringify(run));
    else localStorage.removeItem(SAVED_KEY);
  } catch {
    /* переполненное или запрещённое хранилище — разбор просто не переживёт F5 */
  }
}

function CommentRow({
  comment,
  checked,
  onToggle,
  categories,
}: {
  comment: ModerationComment;
  checked: boolean;
  onToggle: (id: string) => void;
  categories: Record<string, string>;
}) {
  const disabled = comment.goesWithParent;
  const reason = humanReason(comment, categories);
  return (
    <label
      className={`flex gap-3 rounded-lg border p-3 text-sm ${disabled ? 'opacity-60' : 'cursor-pointer hover:bg-muted/40'}`}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => onToggle(comment.id)}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">@{comment.username}</span>
          {comment.verdict.category !== 'ok' && (
            <Badge variant={comment.verdict.action === 'delete' ? 'destructive' : 'secondary'}>
              {categories[comment.verdict.category] ?? comment.verdict.category}
            </Badge>
          )}
          {comment.parentId && <Badge variant="outline">ответ</Badge>}
          {disabled && <Badge variant="outline">уйдёт с родителем</Badge>}
          {comment.likeCount > 0 && (
            <span className="text-xs text-muted-foreground">♡ {comment.likeCount}</span>
          )}
        </div>
        <p className="mt-1 whitespace-pre-wrap break-words">{comment.text}</p>
        <p className={`mt-1 text-xs ${TONE_CLASS[reason.tone]}`} title={comment.verdict.reason}>
          {reason.text}
        </p>
      </div>
    </label>
  );
}

export function CommentModerationPage(): JSX.Element {
  // «Разобрать комментарии» из журнала и карточки поста приходит с ?links=.
  const [searchParams, setSearchParams] = useSearchParams();
  const [links, setLinks] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ClassifyResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [daily, setDaily] = useState<{ limit: number; spent: number | null; batch: number } | null>(null);
  const [restoredAt, setRestoredAt] = useState<string | null>(null);
  const [progress, setProgress] = useState('');
  const [openBuckets, setOpenBuckets] = useState<Record<string, boolean>>({});
  const [journal, setJournal] = useState<DeletionRecord[] | null>(null);
  const [journalOpen, setJournalOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const saved = readSaved();
    const fromUrl = linksFromParam(searchParams.get('links'));
    if (fromUrl) {
      // Ссылки из адреса — в поле; прошлый разбор показываем, только если он про них же.
      setLinks(fromUrl);
      if (saved && saved.links.trim() === fromUrl) {
        setResult(saved.result);
        setSelected(new Set(saved.selected));
        setRestoredAt(saved.at);
      }
      // Из адреса убираем, чтобы F5 не затирал поле, если человек его поправил.
      const next = new URLSearchParams(searchParams);
      next.delete('links');
      setSearchParams(next, { replace: true });
    } else if (saved) {
      setLinks(saved.links);
      setResult(saved.result);
      setSelected(new Set(saved.selected));
      setRestoredAt(saved.at);
    }
    commentModerationApi
      .policy()
      .then((p) => {
        setCategories(p.categories);
        setDaily(p.daily);
      })
      .catch(() => {
        /* подписи и остаток — не повод ронять экран, покажем ключи как есть */
      });
  }, []);

  const groups = useMemo(() => {
    const out: Record<Bucket, ModerationComment[]> = { delete: [], review: [], silent: [], keep: [] };
    for (const c of result?.comments ?? []) {
      // Молчание модели — отдельная корзина, а не «пограничный случай»: иначе
      // прогон, где две сотни комментариев не разобрались, выглядит разобранным.
      const bucket: Bucket = humanReason(c).tone === 'silent' ? 'silent' : c.verdict.action;
      out[bucket]?.push(c);
    }
    return out;
  }, [result]);

  const batchSize = daily?.batch ?? DEFAULT_BATCH;
  const batch = useMemo(
    () => (result ? nextBatch(result.comments, selected, batchSize) : []),
    [result, selected, batchSize],
  );

  async function onClassify() {
    const list = links
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    setResult(null);
    setRestoredAt(null);
    setSelected(new Set());
    saveRun(null);

    const parts: ClassifyResult[] = [];
    try {
      for (const [index, link] of list.entries()) {
        setProgress(`Публикация ${index + 1} из ${list.length}: ${short(link)}`);
        try {
          parts.push(await commentModerationApi.classify([link]));
        } catch (e) {
          // Одна ссылка не разобралась — остальные уже на экране, и терять их
          // из-за неё нельзя: разбор платный и небыстрый.
          parts.push(failedPost(link, (e as Error).message));
        }
        // Показываем по мере готовности, а не в конце.
        setResult(mergeClassify(parts));
      }

      const res = mergeClassify(parts);
      // Галочки ставим по решению модели; ответы под удаляемым родителем
      // отмечать не нужно — они уйдут сами.
      const picked = res.comments
        .filter((c) => c.verdict.action === 'delete' && !c.goesWithParent)
        .map((c) => c.id);
      setSelected(new Set(picked));
      saveRun({ at: new Date().toISOString(), links, result: res, selected: picked });
      const failed = res.posts.filter((p) => p.error);
      if (failed.length) toast.warning(`Не разобрано публикаций: ${failed.length}`);
      if (res.comments.length === 0 && !failed.length) {
        toast.info('Комментариев под этими публикациями нет');
      }
    } finally {
      setBusy(false);
      setProgress('');
    }
  }

  /** Короткий вид ссылки для строки прогресса: паблик и код публикации. */
  function short(url: string): string {
    const m = url.match(/instagram\.com\/([^/]+)\/(?:p|reel|reels|tv)\/([^/?]+)/i);
    return m ? `@${m[1]}/${m[2]}` : url.slice(0, 48);
  }

  async function onDelete() {
    if (!result || !batch.length) return;
    setConfirming(false);
    setBusy(true);
    try {
      const res = await commentModerationApi.remove(
        batch.map((c) => ({
          id: c.id,
          username: postOwner(result, c),
          author: c.username,
          postUrl: c.postUrl,
          text: c.text,
          category: c.verdict.category,
          reason: c.verdict.reason,
        })),
      );
      toast.success(
        `Удалено ${res.deleted.length}` +
          (res.alreadyGone.length ? `, уже не было ${res.alreadyGone.length}` : '') +
          (res.failed.length ? `, не вышло ${res.failed.length}` : ''),
      );
      // Остановку показываем отдельно: это не итог пачки, а причина, по которой
      // остаток не тронут, и человеку решать — ждать или разбираться.
      if (res.stopped) toast.error(res.stopped, { duration: 12_000 });
      if (res.failed.length) toast.warning(res.failed[0].error);
      setDaily((d) => (d ? { ...d, spent: res.spentLastDay } : d));

      const gone = [...res.deleted, ...res.alreadyGone];
      const comments = withoutGone(result.comments, gone);
      const left = new Set(comments.filter((c) => selected.has(c.id)).map((c) => c.id));
      const fresh = { ...result, comments };
      setResult(fresh);
      setSelected(left);
      saveRun({ at: new Date().toISOString(), links, result: fresh, selected: [...left] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function showJournal() {
    const next = !journalOpen;
    setJournalOpen(next);
    if (!next || journal) return;
    try {
      const { entries } = await commentModerationApi.journal();
      setJournal(entries);
    } catch (e) {
      setJournal([]);
      toast.error((e as Error).message);
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** «Отметить всё» / «Снять всё» в корзине — кроме ответов, что уйдут с родителем. */
  function setMany(list: ModerationComment[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of list) {
        if (c.goesWithParent) continue;
        if (on) next.add(c.id);
        else next.delete(c.id);
      }
      return next;
    });
  }

  function forget() {
    setResult(null);
    setSelected(new Set());
    setRestoredAt(null);
    saveRun(null);
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground">
          Вставьте ссылки на публикации, по одной в строке. За раз не больше пяти: разбор идёт
          сразу, и на посте с сотнями комментариев он занимает пару минут.
        </p>
      </div>

      <Textarea
        value={links}
        onChange={(e) => setLinks(e.target.value)}
        rows={5}
        placeholder={'https://www.instagram.com/kazpress/reel/...\nhttps://www.instagram.com/kaztwitter/reel/...'}
        disabled={busy}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onClassify} disabled={busy || !links.trim()}>
          {busy ? 'Разбираю…' : 'Разобрать'}
        </Button>
        {restoredAt && (
          <span className="text-xs text-muted-foreground">
            Показан разбор от {new Date(restoredAt).toLocaleString('ru-RU')} — часть комментариев
            могла измениться.{' '}
            <button type="button" className="underline" onClick={forget}>
              Забыть
            </button>
          </span>
        )}
      </div>

      {busy && (
        <p className="text-sm text-muted-foreground">{progress || 'Разбираю...'}</p>
      )}
      {busy && !result && <Skeleton className="h-64 w-full" />}

      {result && (
        <div className="space-y-6">
          <div className="rounded-lg border p-4 text-sm">
            {result.posts.map((p) => (
              <div key={p.url} className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">{p.url}</span>
                {p.error ? (
                  <Badge variant="destructive">{p.error}</Badge>
                ) : (
                  <>
                    <Badge variant="secondary">{p.total} комментариев</Badge>
                    {p.truncated && (
                      <Badge variant="outline">
                        API отдал не все — остальные в следующий заход
                      </Badge>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {BUCKETS.map(({ key, title, note }) => {
            const list = groups[key];
            if (!list.length) return null;
            // Спорное показываем сразу, спокойное — по нажатию: иначе список на
            // семьсот комментариев прячет то немногое, ради чего его открыли.
            const open = openBuckets[key] ?? (key === 'delete' || key === 'review');
            const selectable = list.filter((c) => !c.goesWithParent);
            const allOn = selectable.length > 0 && selectable.every((c) => selected.has(c.id));
            return (
              <section key={key} className="space-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <button
                    type="button"
                    className="flex items-center gap-2 text-left"
                    onClick={() => setOpenBuckets((prev) => ({ ...prev, [key]: !open }))}
                  >
                    <span className="text-sm font-medium">
                      {title} — {list.length}
                    </span>
                    <span className="text-xs text-muted-foreground underline">
                      {open ? 'свернуть' : 'показать'}
                    </span>
                  </button>
                  {selectable.length > 0 && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline hover:text-foreground"
                      disabled={busy}
                      onClick={() => setMany(selectable, !allOn)}
                    >
                      {allOn ? 'Снять всё' : 'Отметить всё'}
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{note}</p>
                {open && (
                  <div className="space-y-2">
                    {list.map((c) => (
                      <CommentRow
                        key={c.id}
                        comment={c}
                        checked={selected.has(c.id)}
                        onToggle={toggle}
                        categories={categories}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {/* На телефоне снизу таб-бар (fixed, z-40, высота 4rem + safe-area) —
              панель удаления стоит над ним, а не под ним. */}
          <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 space-y-1 border-t bg-background py-3 md:bottom-0">
            <div className="flex items-center gap-3">
              <Button
                variant="destructive"
                onClick={() => setConfirming(true)}
                disabled={busy || !batch.length}
              >
                {busy
                  ? 'Удаляю…'
                  : `Удалить ${batch.length}${selected.size > batch.length ? ` из ${selected.size}` : ''}`}
              </Button>
              <span className="text-xs text-muted-foreground">
                Удаление необратимо. Ответы под удаляемым комментарием уходят вместе с ним.
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {selected.size > batch.length &&
                `За раз удаляем не больше ${batchSize}: дальше Instagram включает антиспам. Остаток — следующим нажатием, лучше через несколько минут. `}
              {daily?.spent != null && `За сутки удалено ${daily.spent} из ${daily.limit}.`}
            </p>
          </div>
        </div>
      )}

      {/*
        Нативный confirm показывал только число. Перед необратимым действием над
        чужими текстами этого мало: человек должен увидеть, что именно уйдёт, —
        и увидеть первым то, в чём модель сомневалась меньше всего.
      */}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить {batch.length} комментариев?</AlertDialogTitle>
            <AlertDialogDescription>
              Отменить это будет нельзя. Ответы под удаляемым комментарием уходят вместе с ним.
              {selected.size > batch.length &&
                ` Остальные ${selected.size - batch.length} останутся на следующую пачку.`}
              {daily?.spent != null && ` За сутки удалено ${daily.spent} из ${daily.limit}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-2 text-sm">
            {batch.map((c) => (
              <div key={c.id} className="border-b pb-2 last:border-0 last:pb-0">
                <p className="text-xs text-muted-foreground">
                  @{c.username} — {humanReason(c, categories).text}
                </p>
                <p className="whitespace-pre-wrap break-words">{c.text}</p>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={() => void onDelete()}>Удалить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <section className="space-y-2 border-t pt-4">
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          onClick={() => void showJournal()}
        >
          <span className="text-sm font-medium">Что удаляли раньше</span>
          <span className="text-xs text-muted-foreground underline">
            {journalOpen ? 'свернуть' : 'показать'}
          </span>
        </button>
        {journalOpen &&
          (journal === null ? (
            <Skeleton className="h-24 w-full" />
          ) : journal.length === 0 ? (
            <p className="text-sm text-muted-foreground">Пока ничего не удаляли.</p>
          ) : (
            <div className="space-y-2">
              {journal.map((e) => (
                <div key={`${e.deletedAt}:${e.commentId}`} className="rounded-lg border p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{new Date(e.deletedAt).toLocaleString('ru-RU')}</span>
                    <span>@{e.account}</span>
                    {e.by && <span>— {e.by}</span>}
                    {e.result === 'already_gone' && <Badge variant="outline">уже не было</Badge>}
                  </div>
                  {e.text && <p className="mt-1 whitespace-pre-wrap break-words">{e.text}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">
                    @{e.author}
                    {e.reason ? ` — ${e.reason}` : ''}
                  </p>
                </div>
              ))}
            </div>
          ))}
      </section>
    </div>
  );
}

/** Ник паблика берём из ссылки на публикацию: по нему бэкенд найдёт токен. */
function postOwner(result: ClassifyResult, comment: ModerationComment): string {
  const post = result.posts.find((p) => p.url === comment.postUrl);
  return post?.username ?? '';
}
